// /app/frontend/src/components/ShopPage.jsx
import React, { useState, useContext, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from '../App';
import { useEntitlements } from '../context/EntitlementsContext';
import apiClient from "../api/client";
import { ArrowLeft, Sparkles, Crown, Check, Shield } from 'lucide-react';
import { isIdentityVerified, getIdentityStatusString, refreshIdentityStatus, getIdentityStatus } from '../api/identity';
import IdentityGateModal from './IdentityGateModal';
import InlineNotice from './common/InlineNotice';


// Products that are not yet available for purchase (Stripe price IDs not configured)
// Remove items from this set once their Stripe prices are created and .env is updated
const DISABLED_PRODUCTS = new Set([
  "ai_wingman_ultra_lifetime",  // Future feature - lifetime Wingman
]);

const PRODUCTS = [
  {
    key: "elite_monthly",
    name: "Elite Membership",
    price: "$19.99 / month",
    tag: "Most Popular",
    type: "subscription",
    description:
      "Enhanced dating features: profile visibility boost, see your admirers, 4 hours of structured event time monthly, and AI conversation coaching.",
    features: [
      "See who's interested in you",
      "Enhanced visibility in discovery",
      "4 hours event time / month",
      "1 hour video room time daily",
      "Weekly profile boost",
      "Read receipts & spotlight",
      "AI conversation coaching",
    ],
  },
  // --- Elite+ (Premium Tier) ---
  {
    key: "elite_plus_monthly",
    name: "Elite+ Membership",
    price: "$39.99 / month",
    tag: "Elite+",
    badge: "Premium",
    type: "subscription",
    description:
      "Premium membership with extended daily video room time, priority feature access, and enhanced visibility. Includes all Elite features.",
    features: [
      "Everything in Elite",
      "2 hours video room time daily",
      "Priority access to new features",
      "Higher visibility & limits",
      "Best for active members",
    ],
  },
  {
    key: "elite_plus_6mo",
    name: "Elite+ (6 months)",
    price: "$203.95 / 6 months",
    tag: "Save 15%",
    badge: "Save",
    type: "subscription",
    description:
      "6-month Elite+ commitment at a discount. Auto-renews every 6 months.",
    features: [
      "Everything in Elite+",
      "6-month auto-renew",
      "~15% savings vs monthly",
    ],
  },
  {
    key: "elite_plus_12mo",
    name: "Elite+ (12 months)",
    price: "$359.91 / year",
    tag: "Best Value",
    badge: "Best Value",
    type: "subscription",
    description:
      "Annual Elite+ at the best price. One year of premium features.",
    features: [
      "Everything in Elite+",
      "Annual auto-renew",
      "~25% savings vs monthly",
    ],
  },
  {
    key: "ai_wingman_pro_monthly",
    name: "AI Conversation Coach",
    price: "$9.99 / month",
    tag: "AI Powered",
    type: "subscription",
    description:
      "AI-powered conversation assistance: message suggestions, profile optimization, and personalized coaching.",
    features: [
      "AI message suggestions",
      "Profile review & tips",
      "Conversation starters",
      "Tone matching assistance",
      "Chat opener generator",
    ],
  },
  {
    key: "ai_wingman_ultra_lifetime",
    name: "AI Coach Lifetime",
    price: "$99 one-time",
    tag: "Coming Soon",
    type: "lifetime",
    comingSoon: true,
    description:
      "Lifetime AI conversation coaching with extended memory and personalized insights.",
    features: [
      "Everything in monthly plan",
      "Extended conversation memory",
      "Pattern insights over time",
      "Future feature access",
    ],
  },
  {
    key: "superlikes_10",
    name: "10 Superlikes Pack",
    price: "$4.99",
    type: "consumable",
    description:
      "Stand out instantly. Send high-signal interest that jumps you to the top of their attention.",
    features: [
      "10 high-impact Superlikes",
      "Great for busy profiles",
      "Stackable with Elite & Wingman",
    ],
  },
  // --- Inner Circle (Speed Dating Premium) ---
  {
    key: "inner_circle_monthly",
    name: "Inner Circle",
    price: "$14.99 / month",
    tag: "Speed Dating+",
    badge: "Premium",
    type: "subscription",
    description:
      "Premium speed dating membership: priority access, guaranteed seats, quality matching, and exclusive Inner Circle events. Minutes reset monthly.",
    features: [
      "6 hours speed dating / month",
      "Early access to events (24hr ahead)",
      "Guaranteed seat reservation",
      "Priority matching algorithm",
      "Inner Circle badge & status",
      "Exclusive IC-only events",
      "Quality-matched pairings",
    ],
  },
  // --- Couple Bundle ---
  {
    key: "couple_bundle_monthly",
    name: "Couple Bundle",
    price: "$29.99 / month",
    tag: "Couples",
    badge: "Couples",
    type: "subscription",
    requiresCouple: true,  // Special flag for couple-only product
    description:
      "Shared premium pool for both partners. Elite benefits for both accounts plus a shared usage wallet.",
    features: [
      "Elite benefits for both partners",
      "Shared usage pool (8 hours/month)",
      "See who liked your couple profile",
      "Priority matching for couples",
      "Couple badge on your profile",
    ],
  },
  {
    key: "speeddating_single",
    name: "Speed Dating Ticket",
    price: "$9.99",
    type: "consumable",
    description:
      "Join a live Unity Love speed dating event and meet multiple people in a short time window.",
    features: [
      "Access to one speed dating session",
      "60 minutes of live participation",
      "Priority pairing by compatibility",
    ],
  },
  {
    key: "speeddating_vip",
    name: "VIP Speed Dating Ticket",
    price: "$14.99",
    tag: "VIP",
    badge: "Priority",
    type: "consumable",
    description:
      "Premium speed dating experience with priority placement and enhanced visibility.",
    features: [
      "Access to one speed dating session",
      "60 minutes of live participation",
      "Priority join ordering",
      "First-round matching priority",
      "Premium visibility badge",
    ],
  },
  {
    key: "lounge_daily",
    name: "Lounge Boost – 1 Hour",
    price: "$3.99",
    type: "consumable",
    description:
      "Boost your exposure in the Lounge for one hour. More visibility during your active window.",
    features: [
      "1 hour boosted visibility",
      "Increased profile exposure",
      "Great for focused connection sessions",
    ],
  },
  // --- Lounge Passes (time-boxed access) ---
  {
    key: "lounge_4h",
    name: "Lounge Pass — 4 Hours",
    price: "$10",
    tag: "Quick Access",
    type: "oneoff",
    description:
      "Unlock the Lifestyle Lounge for 4 hours. Perfect for a quick session.",
    features: [
      "Full Lounge access for 4 hours",
      "One-time purchase",
      "Starts immediately",
    ],
  },
  {
    key: "lounge_24h",
    name: "Lounge Pass — 24 Hours",
    price: "$20",
    tag: "Best Value",
    type: "oneoff",
    description:
      "A full day of Lounge access. Great for weekends or focused connection days.",
    features: [
      "Full Lounge access for 24 hours",
      "One-time purchase",
      "Starts immediately",
    ],
  },
  {
    key: "lounge_7d",
    name: "Lounge Pass — 7 Days",
    price: "$50",
    tag: "Extended Access",
    type: "oneoff",
    description:
      "A full week of Lounge access. Best for serious connectors.",
    features: [
      "Full Lounge access for 7 days",
      "One-time purchase",
      "Starts immediately",
    ],
  },
];

const PRODUCT_TYPE_LABELS = {
  subscription: "Subscription",
  lifetime: "One-time Unlock",
  consumable: "Add-On",
  oneoff: "One-time Access",
};

export default function ShopPage() {
  const { token, user, setUser } = useContext(AuthContext);
  const { getTier, isPremium, hasFeature, entitlements, refreshEntitlements } = useEntitlements();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState("");
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [pendingPurchaseKey, setPendingPurchaseKey] = useState(null);
  
  // Entitlements-based subscription checks
  const effectiveTier = getTier();
  const eliteActive = effectiveTier === 'elite' || effectiveTier === 'elite_plus' || effectiveTier === 'admin';
  const elitePlusActive = effectiveTier === 'elite_plus' || effectiveTier === 'admin';
  const loungePassActive = entitlements?.subscription?.source === 'lounge_pass';
  const loungeExpiresAt = entitlements?.subscription?.expires_at;
  
  // Couple state
  const [coupleInfo, setCoupleInfo] = useState(null);
  const [loadingCouple, setLoadingCouple] = useState(true);
  
  // Handle focus query param for auto-scrolling to specific product
  const focusTier = searchParams.get('focus');
  const focusReason = searchParams.get('reason');
  const planParam = searchParams.get('plan'); // For /shop?plan=inner-circle
  
  // Handle identity return flow
  const identityReturn = searchParams.get('identity');
  
  // Handle verify flow with return URL
  const verifyMode = searchParams.get('verify') === '1';
  const returnUrl = searchParams.get('return');
  
  // Products that require identity verification
  const IDENTITY_REQUIRED_PRODUCTS = new Set([
    'elite_monthly',
    'elite_plus_monthly',
    'elite_plus_6mo',
    'elite_plus_12mo',
    'inner_circle_monthly',
  ]);
  
  // Fetch couple info for couple bundle purchases
  useEffect(() => {
    const fetchCoupleInfo = async () => {
      if (!token) return;
      try {
        const res = await apiClient.get(`/couples/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCoupleInfo(res.data.couple);
      } catch (err) {
        console.error("Failed to fetch couple info:", err);
      } finally {
        setLoadingCouple(false);
      }
    };
    fetchCoupleInfo();
  }, [token]);
  
  // Track if we're checking identity status (to prevent flash of locked state)
  const [isCheckingIdentity, setIsCheckingIdentity] = useState(identityReturn === 'return');
  
  // Check and refresh identity status when returning from Stripe Identity
  useEffect(() => {
    if (identityReturn === 'return' && token) {
      setIsCheckingIdentity(true);
      const checkIdentity = async () => {
        try {
          // Refresh identity status from Stripe/DB
          const result = await refreshIdentityStatus(token);
          
          // Update user context with new identity status
          if (result?.identity && setUser) {
            setUser(prevUser => ({
              ...prevUser,
              identity: result.identity
            }));
            
            // If verified and we have a return URL, navigate back
            if (result.identity?.status === 'verified' && returnUrl) {
              navigate(returnUrl);
              return;
            }
          }
        } catch (e) {
          console.error('Failed to refresh identity status:', e);
        } finally {
          setIsCheckingIdentity(false);
        }
      };
      checkIdentity();
    }
  }, [identityReturn, token, setUser, returnUrl, navigate]);
  
  // Auto-scroll to focused product on mount
  React.useEffect(() => {
    // Support both ?focus= and ?plan= params
    const targetTier = focusTier || planParam;
    
    if (targetTier) {
      // Map tier/plan to product key
      const tierToKey = {
        'elite': 'elite_monthly',
        'elite_monthly': 'elite_monthly',
        'wingman_pro': 'ai_wingman_pro_monthly',
        'ai_wingman_pro_monthly': 'ai_wingman_pro_monthly',
        'inner-circle': 'inner_circle_monthly',
        'inner_circle': 'inner_circle_monthly',
        'inner_circle_monthly': 'inner_circle_monthly',
      };
      const productKey = tierToKey[targetTier] || targetTier;
      
      // Scroll to product card after a brief delay for render
      setTimeout(() => {
        const element = document.getElementById(`product-${productKey}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight animation - pulse effect
          element.classList.add('ring-2', 'ring-pink-500', 'ring-offset-2', 'ring-offset-slate-950');
          element.style.transition = 'all 0.3s ease';
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-pink-500', 'ring-offset-2', 'ring-offset-slate-950');
          }, 2000);
        }
      }, 100);
    }
  }, [focusTier, planParam]);

  const handlePurchase = async (productKey) => {
    setError("");
    
    // Check if this product requires identity verification
    if (IDENTITY_REQUIRED_PRODUCTS.has(productKey) && !isIdentityVerified(user)) {
      // Store the pending purchase and show identity modal
      setPendingPurchaseKey(productKey);
      setShowIdentityModal(true);
      return;
    }
    
    // Check if this is a couple bundle and user is not in an active couple
    if (productKey === 'couple_bundle_monthly') {
      if (!coupleInfo || coupleInfo.status !== 'active') {
        setError("You need an active couple profile to purchase the Couple Bundle. Go to Settings > Couple Profile to create one.");
        return;
      }
    }
    
    // Proceed with checkout
    await proceedToCheckout(productKey);
  };
  
  const proceedToCheckout = async (productKey) => {
    setLoadingKey(productKey);

    try {
      // Handle couple bundle separately
      if (productKey === 'couple_bundle_monthly') {
        if (!coupleInfo?.id) {
          throw new Error("No active couple profile");
        }
        
        const res = await apiClient.post(
          `/couples/billing/checkout`,
          { couple_id: coupleInfo.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
        } else {
          throw new Error("No checkout URL returned");
        }
        return;
      }
      
      // Map product keys to the billing system's expected format
      const productKeyMap = {
        'elite_monthly': 'elite',
        'ai_wingman_pro_monthly': 'ai_wingman_pro',
        'superlikes_10': 'superlikes_10',
        'speeddating_single': 'speeddating_single',
        'lounge_daily': 'lounge_daily',
        // Elite+ (NEW)
        'elite_plus_monthly': 'elite_plus_monthly',
        'elite_plus_6mo': 'elite_plus_6mo',
        'elite_plus_12mo': 'elite_plus_12mo',
        // Inner Circle (Speed Dating Premium)
        'inner_circle_monthly': 'inner_circle_monthly',
        // Lounge Passes
        'lounge_4h': 'lounge_4h',
        'lounge_24h': 'lounge_24h',
        'lounge_7d': 'lounge_7d',
      };

      const mappedKey = productKeyMap[productKey] || productKey;

      // Call existing billing checkout endpoint
      const res = await apiClient.post(
        `/billing/create-checkout`,
        { 
          product: mappedKey,
          success_url: `${window.location.origin}/shop?success=true`,
          cancel_url: `${window.location.origin}/shop?canceled=true`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      console.error(e);
      if (e.response?.status === 403 && e.response?.data?.detail?.includes('Identity verification')) {
        // Server-side identity gate triggered - show modal
        setPendingPurchaseKey(productKey);
        setShowIdentityModal(true);
      } else if (e.response?.status === 400) {
        setError(e.response?.data?.detail || "Checkout configuration issue. Please contact support.");
      } else {
        setError(
          "Something went wrong starting checkout. Try again in a bit or contact support."
        );
      }
    } finally {
      setLoadingKey(null);
    }
  };
  
  // Handler for when identity verification completes
  const handleIdentityVerified = () => {
    if (pendingPurchaseKey) {
      proceedToCheckout(pendingPurchaseKey);
      setPendingPurchaseKey(null);
    }
  };
  
  // Identity verification status for display
  // While checking (return from Stripe), show as "checking" to prevent flash
  const identityStatus = isCheckingIdentity ? 'checking' : getIdentityStatusString(user);
  const userVerified = !isCheckingIdentity && isIdentityVerified(user);

  // Check user tier status (from EntitlementsProvider, defined at top)
  const hasWingmanPro = user?.ai_wingman_pro || false;
  
  // Lounge pass status
  const loungeIncluded = eliteActive || elitePlusActive;

  // Filter visible products based on user's current tier
  const visibleProducts = useMemo(() => {
    let filtered = PRODUCTS;
    
    // If already Elite+, hide all Elite/Elite+ purchase options
    if (elitePlusActive) {
      filtered = filtered.filter(
        (p) => !["elite_monthly", "elite_plus_monthly", "elite_plus_6mo", "elite_plus_12mo"].includes(p.key)
      );
    }
    // If Elite but not Elite+, hide regular Elite (show Elite+ upgrade options)
    else if (eliteActive && !elitePlusActive) {
      filtered = filtered.filter(
        (p) => p.key !== "elite_monthly"
      );
    }
    
    // Hide lounge passes if lounge is already included (Elite/Elite+) or an active pass exists
    if (loungeIncluded || loungePassActive) {
      filtered = filtered.filter(
        (p) => !p.key.startsWith("lounge_")
      );
    }

    return filtered;
  }, [eliteActive, elitePlusActive, loungeIncluded, loungePassActive]);

  return (
    <div className="w-full h-full overflow-y-auto bg-slate-950/90 text-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* Elite+ Status Banner */}
        {elitePlusActive ? (
          <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold text-emerald-400">
              <Crown className="w-5 h-5" />
              You&apos;re Elite+ ✅
            </div>
            <div className="text-sm text-emerald-300/80 mt-1">Your account has Elite+ access. You have all premium features.</div>
          </div>
        ) : eliteActive ? (
          <div className="mb-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold text-rose-400">
              <Sparkles className="w-5 h-5" />
              Upgrade to Elite+ ✨
            </div>
            <div className="text-sm text-rose-300/80 mt-1">You&apos;re Elite. Upgrade to Elite+ for the full power-user package.</div>
          </div>
        ) : null}

        {/* Lounge Pass Active Banner (only for non-Elite users with active pass) */}
        {!loungeIncluded && loungePassActive && loungeExpiresAt ? (
          <div className="mb-4 rounded-2xl border border-violet-500/30 bg-violet-500/10 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold text-violet-400">
              🛋️ Lounge Pass Active
            </div>
            <div className="text-sm text-violet-300/80 mt-1">
              Access until <span className="text-violet-200 font-medium">{new Date(loungeExpiresAt).toLocaleString()}</span>
            </div>
          </div>
        ) : null}

        {/* Identity Verification Status Banner */}
        {!eliteActive && !elitePlusActive && (
          userVerified ? (
            <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
              <div className="flex items-center gap-2 font-semibold text-emerald-400">
                <Shield className="w-5 h-5" />
                Identity Verified ✓
              </div>
              <div className="text-sm text-emerald-300/80 mt-1">
                You&apos;re verified and ready to purchase Elite memberships.
              </div>
            </div>
          ) : identityStatus === 'checking' ? (
            <div className="mb-4 rounded-2xl border border-slate-500/30 bg-slate-500/10 px-4 py-3">
              <div className="flex items-center gap-2 font-semibold text-slate-300">
                <Shield className="w-5 h-5 animate-pulse" />
                Verifying...
              </div>
              <div className="text-sm text-slate-400 mt-1">
                Checking your identity verification status...
              </div>
            </div>
          ) : identityStatus === 'pending' ? (
            <div className="mb-4 rounded-2xl border border-blue-500/30 bg-blue-500/10 px-4 py-3">
              <div className="flex items-center gap-2 font-semibold text-blue-400">
                <Shield className="w-5 h-5" />
                Verification Pending
              </div>
              <div className="text-sm text-blue-300/80 mt-1">
                Your identity verification is being processed. 
                <button 
                  onClick={() => setShowIdentityModal(true)}
                  className="ml-1 underline hover:text-blue-200"
                >
                  Check status
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <div className="flex items-center gap-2 font-semibold text-amber-400">
                <Shield className="w-5 h-5" />
                Identity Verification Required
              </div>
              <div className="text-sm text-amber-300/80 mt-1">
                Verify your identity to unlock Elite membership purchases.{' '}
                <button 
                  onClick={() => setShowIdentityModal(true)}
                  className="underline hover:text-amber-200"
                >
                  Verify now
                </button>
              </div>
            </div>
          )
        )}

        <header className="mb-6 sm:mb-8">
          {/* Back to Dashboard button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors mb-4"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Dashboard</span>
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Unity Love Shop
          </h1>
          <p className="text-sm sm:text-base text-slate-300 mt-1">
            Upgrade your experience with Elite access, AI Wingman, and boosts
            that amplify your connections.
          </p>
        </header>

        {error && (
          <div className="mb-4">
            <InlineNotice
              variant="error"
              title="Checkout issue"
              actionLabel="Try again"
              onAction={() => setError("")}
              testId="shop-inline-notice"
            >
              {error}
            </InlineNotice>
          </div>
        )}

        {/* Contextual message when coming from locked feature */}
        {focusReason === 'locked_feature' && focusTier && (
          <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            👑 Upgrade to {focusTier === 'elite' || focusTier === 'elite_monthly' ? 'Elite' : 'Wingman Pro'} to unlock this feature
          </div>
        )}

        {/* Success message from Stripe redirect */}
        {new URLSearchParams(window.location.search).get('success') === 'true' && (
          <div className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            🎉 Purchase successful! Your upgrade will be active within a few moments.
          </div>
        )}

        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {visibleProducts.map((product) => {
            const isLoading = loadingKey === product.key;
            // Disable if marked comingSoon OR if in the DISABLED_PRODUCTS set
            const isDisabled = product.comingSoon || DISABLED_PRODUCTS.has(product.key);
            
            // Check if user already has this subscription
            const alreadyOwned = 
              (product.key === 'elite_monthly' && eliteActive) ||
              (product.key === 'ai_wingman_pro_monthly' && hasWingmanPro);
            
            // Is this an Elite+ product that's an upgrade for current Elite users?
            const isElitePlusUpgrade = eliteActive && !elitePlusActive && product.key.startsWith('elite_plus');
            
            // Does this product require identity verification?
            const requiresIdentity = IDENTITY_REQUIRED_PRODUCTS.has(product.key);
            const needsVerification = requiresIdentity && !userVerified;

            return (
              <div
                key={product.key}
                id={`product-${product.key}`}
                className={`relative flex flex-col rounded-2xl border bg-slate-900/70 p-4 sm:p-5 shadow-lg shadow-black/40 transition-all duration-300 ${
                  product.tag === "Most Popular"
                    ? "border-pink-500/70"
                    : product.tag === "Elite+" || product.badge === "Premium"
                    ? "border-amber-500/50"
                    : product.tag === "Best Value"
                    ? "border-emerald-500/50"
                    : "border-slate-700/70"
                }`}
              >
                {/* Tag badge */}
                {(product.tag || isDisabled) && (
                  <div
                    className={`absolute -top-3 left-4 inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${
                      isDisabled
                        ? "bg-slate-600 text-slate-300"
                        : product.tag === "Most Popular"
                        ? "bg-pink-500 text-white"
                        : product.tag === "Elite+" || product.badge === "Premium"
                        ? "bg-amber-500 text-white"
                        : product.tag === "Best Value"
                        ? "bg-emerald-500 text-white"
                        : product.tag === "Save 15%"
                        ? "bg-blue-500 text-white"
                        : "bg-indigo-500 text-white"
                    }`}
                  >
                    {isDisabled ? "Coming Soon" : product.tag}
                  </div>
                )}

                {alreadyOwned && (
                  <div className="absolute -top-3 right-4 inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold bg-emerald-500 text-white">
                    ✓ Active
                  </div>
                )}
                
                {/* Identity verification required indicator - hide while checking to prevent flash */}
                {needsVerification && !alreadyOwned && !isCheckingIdentity && (
                  <div className="absolute -top-3 right-4 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    <Shield className="w-3 h-3" />
                    <span>ID Required</span>
                  </div>
                )}

                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold">
                      {product.name}
                    </h2>
                    <p className="mt-1 text-sm text-slate-300">
                      {product.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-base sm:text-lg font-bold text-pink-300">
                      {product.price}
                    </div>
                    <div className="text-xs text-slate-400">
                      {PRODUCT_TYPE_LABELS[product.type] || ""}
                    </div>
                  </div>
                </div>

                {product.features?.length ? (
                  <ul className="mt-3 space-y-1.5 text-xs sm:text-sm text-slate-200">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-400" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    disabled={isDisabled || isLoading || alreadyOwned}
                    onClick={() => handlePurchase(product.key)}
                    data-testid={`buy-${product.key.replace(/_/g, '-')}`}
                    className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-xs sm:text-sm font-semibold transition ${
                      alreadyOwned
                        ? "cursor-default bg-emerald-600/20 text-emerald-300 border border-emerald-500/30"
                        : isDisabled
                        ? "cursor-not-allowed bg-slate-700 text-slate-400"
                        : product.tag === "Most Popular"
                        ? "bg-pink-500 hover:bg-pink-400 text-white"
                        : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-600"
                    }`}
                  >
                    {alreadyOwned
                      ? "Active"
                      : isDisabled
                      ? "Coming Soon"
                      : isLoading
                      ? "Starting checkout..."
                      : product.type === "subscription"
                      ? "Subscribe"
                      : "Buy Now"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-[11px] sm:text-xs text-slate-400">
          All purchases are handled securely via Stripe. Subscriptions renew automatically
          each month until cancelled in your settings. Need help? Contact support.
        </div>
      </div>
      
      {/* Identity Verification Modal */}
      <IdentityGateModal
        isOpen={showIdentityModal}
        onClose={() => {
          setShowIdentityModal(false);
          setPendingPurchaseKey(null);
        }}
        onVerified={handleIdentityVerified}
      />
    </div>
  );
}
